import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import { FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { FormlyBootstrapModule } from "@ngx-formly/bootstrap";
import {
  FormlyFieldConfig,
  FormlyFormOptions,
  FormlyModule,
} from "@ngx-formly/core";
import { interval, map, switchMap, takeWhile } from "rxjs";
import { LlmTestCasesModalComponent } from "../../../../components/modal-templates/llm-test-cases-modal/llm-test-cases-modal.component";
import { ProgressModalComponent } from "../../../../components/modal-templates/progress-modal/progress-modal.component";
import { RestService } from "../../../../core/services/rest.service";
import { ToasterService } from "../../../../core/services/toaster-service";
import { MaterialModule } from "../../../../core/shared/material.module";
import { MessageEnum } from "../../../../core/utils/message.enum";

@Component({
  selector: "app-side-panel-view",
  standalone: true,
  imports: [
    CommonModule,
    FormlyModule,
    FormlyBootstrapModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
  ],
  templateUrl: "./side-panel-view.component.html",
  styleUrl: "./side-panel-view.component.scss",
})
export class SidePanelViewComponent implements OnInit {
  private modalService = inject(NgbModal);

  @Input()
  projectId!: string;

  @Output() onLoadVersions = new EventEmitter<void>();

  form = new FormGroup({});
  options: FormlyFormOptions = {};
  isFormValid = false;

  model: any = {
    name: "",
    applicationUrl: "",
    language: "",
    isAuthenticated: false,
    userName: "",
    password: "",
    testCaseVersionId: "",
  };

  fields: FormlyFieldConfig[] = [
    {
      key: "name",
      type: "input",
      templateOptions: {
        label: "Project Name",
        placeholder: "Enter Project Name",
        required: true,
      },
    },
    {
      key: "applicationUrl",
      type: "input",
      templateOptions: {
        label: "Application URL",
        placeholder: "Enter Application URL",
        required: true,
      },
    },
    {
      key: "language",
      type: "select",
      templateOptions: {
        label: "Language Selection",
        placeholder: "Select Language",
        required: true,
        options: [
          { label: "Java Selenium", value: "java" },
          { label: "Python", value: "python" },
        ],
      },
    },
    {
      key: "isAuthenticated",
      type: "radio",
      templateOptions: {
        label: "Authentication Details",
        required: true,
        options: [
          { value: true, label: "Yes" },
          { value: false, label: "No" },
        ],
        labelProp: "label",
        valueProp: "value",
      },
      defaultValue: "yes",
      className: "formly-radio-inline",
    },
    {
      key: "userName",
      type: "input",
      templateOptions: {
        label: "Username",
        placeholder: "Enter Username",
        required: true,
      },
      hideExpression: (model) => model.isAuthenticated !== true, // show when 'Yes' is selected
    },
    {
      key: "password",
      type: "input",
      templateOptions: {
        type: "password",
        label: "Password",
        placeholder: "Enter Password",
        required: true,
      },
      hideExpression: (model) => model.isAuthenticated !== true, // show when 'Yes' is selected
    },
    {
      key: "testCaseVersionId",
      type: "select",
      templateOptions: {
        label: "Test Suit Version",
        placeholder: "Select Version",
        required: true,
        options: [],
      },
    },
  ];

  statusPercentageMap: any = {
    TESTCASE_PROCESSING: { min: 0, max: 5, label: "Testcase Processing" },
    CRAWLING: { min: 5, max: 20, label: "Crawling" },
    TESTDATA_GENERATING: { min: 20, max: 40, label: "Testdata Generating" },
    TEST_SCRIPT_GENERATING: {
      min: 40,
      max: 90,
      label: "Test Script Generating",
    },
    COMPLETED: { min: 100, max: 100, label: "Completed" },
  };

  currentPercentage = 0;
  lastStatus = null;

  selectedDetails: any;

  constructor(private restService: RestService, private readonly toaster: ToasterService) {}

  ngOnInit(): void {
    this.form.statusChanges.subscribe((status) => {
      this.isFormValid = status === "VALID";
    });

    this.loadTestSuiteVersions();
  }

  loadTestSuiteVersions() {
    this.restService
      .getTestCaseVersions(this.projectId)
      .subscribe((versions: any) => {
        const testSuiteVersionField = this.fields.find(
          (field) => field.key === "testCaseVersionId"
        );

        if (testSuiteVersionField) {
          testSuiteVersionField.templateOptions!.options = versions?.data.map(
            (version: any) => ({
              label: version.versionName,
              value: version._id,
            })
          );
        }
      });
  }

  onGenerateTestScripts() {
    const configs = {
      title: "Generating Test Scripts",
      data: {},
    };

    const modalRef = this.modalService.open(ProgressModalComponent, {
      centered: true,
      windowClass: "secondary-modal",
      size: "lg",
      backdrop: "static",
    });

    console.log(this.form?.value);
    const payload = {
      ...this.form?.value,
      testCaseIds: this.getTestCaseIDs(),
    };

    this.restService.generateTestCasesByLLM(this.projectId, payload).subscribe(
      (response: any) => {
        console.log(response);
        const versionId = response?.data?.id;
        if (versionId) {
          this.restService
            .triggerJobByLLM(this.projectId, versionId)
            .subscribe(() => {
              interval(5000)
                .pipe(
                  switchMap(() =>
                    this.restService
                      .getLLMStatus(this.projectId, versionId)
                      .pipe(
                        map((statusResponse: any) => {
                          const status = statusResponse?.data;

                          const range = this.statusPercentageMap[status] || {
                            min: 0,
                            max: 0,
                          };

                          if (
                            status !== this.lastStatus &&
                            this.currentPercentage < range.min
                          ) {
                            this.currentPercentage = range.min;
                          }

                          if (this.currentPercentage < range.max) {
                            this.currentPercentage += 3;
                          }

                          if (this.currentPercentage > range.max) {
                            this.currentPercentage = range.max;
                          }

                          modalRef.componentInstance.progressText =
                            statusResponse?.data?.message || status;
                          modalRef.componentInstance.percentage =
                            this.currentPercentage;

                          modalRef.componentInstance.progressText =
                            this.statusPercentageMap[status]?.label;

                          if (status === "COMPLETED") {
                            return { done: true, isCompleted: true };
                          }

                          if (status === "ERROR") {
                            modalRef.dismiss("error");
                            this.toaster.pop({ type: 'success', text: MessageEnum.TEST_SCRIPTS_GENERATION_FAILURE });
                            return { done: true };
                          }

                          // Still in progress
                          return { done: false };
                        })
                      )
                  ),
                  takeWhile((result) => !result.done, true)
                )
                .subscribe({
                  next: (result) => {
                    if (result?.done && result?.isCompleted) {
                      console.log("Final result:", result);
                      this.onLoadVersions.emit();
                      modalRef.dismiss("success");
                      this.toaster.pop({ type: 'success', text: MessageEnum.TEST_SCRIPTS_GENERATION_SUCCESS });
                      this.gettingTestScriptsDetails(this.projectId, versionId);
                    }
                  },
                  error: (err) => {
                    console.error("Error during polling:", err);
                    modalRef.dismiss("error");
                    this.toaster.pop({ type: 'error', text: MessageEnum.TEST_SCRIPTS_GENERATION_FAILURE });
                  },
                });
            });
        }
      },
      (error) => {
        console.error(error);
        modalRef.dismiss("error");
        this.toaster.pop({ type: 'error', text: MessageEnum.TEST_SCRIPTS_GENERATION_FAILURE });
      }
    );

    modalRef.componentInstance.configs = configs;
    modalRef.result.then((result) => {
      this.selectedDetails = result;
    });
  }

  gettingTestScriptsDetails(projectId: string, versionId: string) {
    this.restService
      .getLLMGeneratedTestScripts(projectId)
      .subscribe((response) => {
        console.log(response);
      });
  }

  getTestCaseIDs() {
    let tcIds: any = [];
    this.selectedDetails?.selectedTestCaseDetails.forEach((element: any) => {
      tcIds.push(element?.testCaseId);
    });

    return tcIds;
  }

  viewTestCases() {
    var configs = {
      title: "Select Test Cases",
      data: {
        ...this.form?.value,
        projectId: this.projectId,
        selectedTestCases: this.selectedDetails?.selectedTestCaseDetails,
      },
    };

    const modalRef = this.modalService.open(LlmTestCasesModalComponent, {
      centered: true,
      size: "xl",
      backdrop: 'static',
    });
    modalRef.componentInstance.configs = configs;
    modalRef.result.then((result) => {
      this.selectedDetails = result;
      console.log(result);
    });
  }
}
